/**
 * orgResourcesApi — Symfony API client for org-scoped reference data the
 * new React calendar pages need at boot (resources, locations, custom
 * fields, pilots, mechanics).
 *
 * Like `userApi.js` this module uses **named function exports** so the
 * bootstrap thunk can pull the helpers in directly and Jest can mock them
 * at the module boundary.
 *
 * Each helper resolves the org slug via `BaseApi.buildOrgEndpoint(...)` —
 * NEVER hard-code `/api/v1/...` paths (the base URL already includes the
 * `/api/v1` prefix; doing it again triggers the documented "double-prefix
 * bug" in CLAUDE.md → "API Patterns").
 *
 * Endpoints (verified 2026-04-08):
 *   - GET `/api/v1/org/{org}/resource`        → OrganizationResourcesResponseDto
 *   - GET `/api/v1/org/{org}/location`        → Location[]
 *   - GET `/api/v1/org/{org}/custom-fields`   → CustomField[]
 *   - GET `/api/v1/org/{org}/pilots`          → OrganizationUserResponseDto[]
 *   - GET `/api/v1/org/{org}/mechanics`       → OrganizationUserResponseDto[]
 *   - GET `/api/v1/org/{org}/activity-types`  → ActivityType[] ({id,name,color,trash})
 *
 * @module orgResourcesApi
 */

import BaseApi from './BaseApi.js';

/** GET /api/v1/org/{org}/resource — full resource list (aircraft, sims, instructors). */
export function listResources() {
	return BaseApi.get(BaseApi.buildOrgEndpoint('/resource'));
}

/** GET /api/v1/org/{org}/location — locations the org operates from. */
export function listLocations() {
	return BaseApi.get(BaseApi.buildOrgEndpoint('/location'));
}

/** GET /api/v1/org/{org}/custom-fields — org-defined custom event fields. */
export function listCustomFields() {
	return BaseApi.get(BaseApi.buildOrgEndpoint('/custom-fields'));
}

/** GET /api/v1/org/{org}/pilots — pilots in the PILOT_GROUP for filter pickers. */
export function listPilots() {
	return BaseApi.get(BaseApi.buildOrgEndpoint('/pilots'));
}

/** GET /api/v1/org/{org}/mechanics — mechanics / instructors for filter pickers. */
export function listMechanics() {
	return BaseApi.get(BaseApi.buildOrgEndpoint('/mechanics'));
}

/**
 * GET /api/v1/org/{org}/activity-types — org activity types used to color
 * event chips by activity type. Each entry carries `{ id, name, color, trash }`.
 * Org-scoped via `buildOrgEndpoint` — resolves to the current dashboard org.
 */
export function listActivityTypes() {
	return BaseApi.get(BaseApi.buildOrgEndpoint('/activity-types'));
}
